import gql from 'graphql-tag'
import PropTypes from 'prop-types'
import { Query } from '@apollo/client/react/components'
import { useQuery } from '@apollo/client'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from '@components/error'
import { flags } from '@lib/feature-flags'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import useGolfFacilitiesDataSourceEnabled from '@lib/feature-flags/useGolfFacilitiesDataSourceEnabled'
import FacilityPage from './facility-page'

export const FACILITY_QUERY = gql`
query Facility($id: String!) {
  facility(facility_id: $id) {
    facility_id
    facility_name
    state
    state_name
    city
    zip
    phone
    photo_facility
    course_type_label
    operations_type_label
    address1
    address2
    address3
    address4
    universal_id
  }
}
`

export const FACILITY_HOLE_IN_ONES_QUERY = gql`
query FacilityHoleInOnes($facility_legacy_ids: [String!]!, $limit: Int, $offset: Int) {
  facilityHoleInOnes(facilityLegacyIds: $facility_legacy_ids, limit: $limit, offset: $offset) {
    entries {
      id
      golferName
      golferHometown
      golferEmail
      golferPhone
      gender
      age
      handicap
      yearsPlaying
      totalHios
      occurredAt
      courseName
      holeNumber
      yardage
      clubUsed
      clubBrand
      clubModel
      ballUsed
      description
      witnesses
      golferSocialHandles {
        service
        username
      }
      marketingOptIn
      displayOptIn
      publicVisibility
      featuredPhotoUrl
      photoUrls
      videoUrls
      certificateUrl
      createdAt
      updatedAt
      pgaProfessional {
        id
        displayName
        email
        pgaId
        classification
        currentStatus
      }
      location {
        id
        name
        addresses {
          id
          address1
          city
          state
          postalCode
        }
      }
      golfer {
        id
        displayName
        email
        pgaId
      }
    }
    totalCount
    hasMore
  }
}
`

function FacilityPageQuery(props) {
  const [isHoleInOneEnabled] = useFeatureFlags([flags.FLAG_MEMBERSHIP_HOLE_IN_ONE])
  const golfFacilitiesDataSource = useGolfFacilitiesDataSourceEnabled()
  const { id: facilityLegacyId, ssrData } = props

  // Helper to render facility with data
  const renderFacility = (response) => {
    if (!response) {
      return <Error statusCode={404} />
    }

    if (!isHoleInOneEnabled) {
      return (
        <FacilityPage
          {...props}
          {...response}
          holeInOnes={null}
          holeInOnesLoading={false}
          holeInOnesError={null}
        />
      )
    }

    return (
      <FacilityPageWithHoleInOnes
        {...props}
        {...response}
        facilityLegacyId={facilityLegacyId}
      />
    )
  }

  // If SSR data is provided, render directly
  if (ssrData !== undefined) {
    return renderFacility(ssrData)
  }

  if (golfFacilitiesDataSource) {
    return <Error statusCode={404} displayErrorType="page" />
  }

  // Fallback to client-side fetching
  return (
    <Query query={FACILITY_QUERY} variables={props} context={{ clientName: 'pga-algolia' }}>
      {({
        loading, error, data,
      }) => {
        if (error) {
          return <Error error={error} displayErrorType="page" />
        }
        if (loading) return <LinearProgress color="secondary" />
        return renderFacility(data.facility)
      }}
    </Query>
  )
}

FacilityPageQuery.propTypes = {
  id: PropTypes.string,
  ssrData: PropTypes.object,
}

export default FacilityPageQuery

function FacilityPageWithHoleInOnes(props) {
  const { facilityLegacyId } = props

  const {
    data: holeInOnesData,
    loading: holeInOnesLoading,
    error: holeInOnesError,
  } = useQuery(FACILITY_HOLE_IN_ONES_QUERY, {
    variables: {
      facility_legacy_ids: [facilityLegacyId],
      limit: 10,
      offset: 0,
    },
    context: { clientName: 'membership' },
    errorPolicy: 'partial',
  })

  const holeInOnes = holeInOnesData?.facilityHoleInOnes || null

  return (
    <FacilityPage
      {...props}
      holeInOnes={holeInOnes}
      holeInOnesLoading={holeInOnesLoading}
      holeInOnesError={holeInOnesError}
    />
  )
}

FacilityPageWithHoleInOnes.propTypes = {
  facilityLegacyId: PropTypes.string.isRequired,
}
